import type { FastifyReply, FastifyRequest } from 'fastify'
import { z } from 'zod'
import { getFirestore } from '../../core/database/firebase.js'
import { AdminRewardsService } from './admin.rewards.service.js'
import type { RewardEventType, UpdateRewardRuleDTO } from '@my-roadtrip/types'
import { ApiResponse } from '../../utils/response'

const eventParamSchema = z.object({
  event: z.string().trim() as z.ZodType<RewardEventType>
})

const updateRuleSchema: z.ZodSchema<UpdateRewardRuleDTO> = z.object({
  enabled: z.boolean().optional(),
  points: z.number().optional(),
  fuel: z.number().optional(),
  xp: z.number().optional(),
  seasonXP: z.number().optional(),
  badge: z.string().trim().max(120).optional(),
  segment: z.enum(['all', 'free', 'premium', 'pro']).optional(),
  description: z.string().trim().max(240).optional()
})

export class AdminRewardsController {
  private readonly service = new AdminRewardsService(getFirestore())

  async getConfig(_req: FastifyRequest, reply: FastifyReply) {
    const config = await this.service.getConfig()
    return reply.send(config)
  }

  async getAllRules(_req: FastifyRequest, reply: FastifyReply) {
    const rules = await this.service.getAllRules()
    return reply.send(rules)
  }

  async getRule(request: FastifyRequest, reply: FastifyReply) {
    try {
      const { event } = eventParamSchema.parse(request.params ?? {})
      const rule = await this.service.getRule(event)
      return reply.send(rule)
    } catch (error) {
      return ApiResponse.badRequest(reply, 'Invalid event')
    }
  }

  async updateRule(request: FastifyRequest, reply: FastifyReply) {
    try {
      const { event } = eventParamSchema.parse(request.params ?? {})
      const payload = updateRuleSchema.parse(request.body ?? {})
      const actorId = (request as any)?.user?.uid as string | undefined
      const rule = await this.service.updateRule(event, payload, actorId)
      return reply.send(rule)
    } catch (error) {
      if (error instanceof z.ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid reward payload')
      }
      request.log.error(error, 'admin_update_reward_rule_failed')
      return ApiResponse.internalError(reply, 'Failed to update rule')
    }
  }

  async batchUpdateRules(request: FastifyRequest, reply: FastifyReply) {
    const schema = z.object({
      updates: z.array(
        z.object({
          event: z.string().trim() as z.ZodType<RewardEventType>,
          data: updateRuleSchema
        })
      )
    })
    try {
      const { updates } = schema.parse(request.body ?? {})
      const actorId = (request as any)?.user?.uid as string | undefined
      const rules = await this.service.batchUpdateRules(updates, actorId)
      return reply.send(rules)
    } catch (error) {
      if (error instanceof z.ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid batch payload')
      }
      request.log.error(error, 'admin_batch_update_rewards_failed')
      return ApiResponse.internalError(reply, 'Failed to batch update rules')
    }
  }

  async resetConfig(request: FastifyRequest, reply: FastifyReply) {
    const actorId = (request as any)?.user?.uid as string | undefined
    const config = await this.service.resetToDefaults(actorId)
    return reply.send(config)
  }

  async getStatistics(request: FastifyRequest, reply: FastifyReply) {
    const days = Number((request.query as { days?: string })?.days ?? 30)
    const stats = await this.service.getStatistics(Number.isFinite(days) ? days : 30)
    return reply.send(stats)
  }
}
